import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { ChatService, ChatMessage, ChatRoom } from '../../services/chat.service';
import { WebSocketService } from '../../services/websocket.service';
import { AuthService } from '../../services/auth.service';
import { ProximityService } from '../../services/proximity.service';

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements OnInit, OnDestroy {
  @ViewChild('messagesContainer', { static: false }) messagesContainer!: ElementRef;
  
  isConnected = false;
  currentMessage = '';
  messages: ChatMessage[] = [];
  activeRooms: ChatRoom[] = [];
  selectedRoom: ChatRoom | null = null;
  isProximityEnabled = false;
  isLoading = false;

  private subscriptions = new Subscription();

  constructor(
    private chatService: ChatService,
    private wsService: WebSocketService,
    private authService: AuthService,
    private proximityService: ProximityService
  ) {}

  ngOnInit(): void {
    this.initializeServices();
    this.setupSubscriptions();
    this.connectWebSocket();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.wsService.disconnect();
  }

  private initializeServices(): void {
    const user = this.authService.getCurrentUser();
    if (user?.id) {
      this.chatService.setCurrentUser(user.id);
    }
  }

  private setupSubscriptions(): void {
    // WebSocket connection status
    this.subscriptions.add(
      this.wsService.connectionStatus$.subscribe(connected => {
        this.isConnected = connected;
        if (connected && this.selectedRoom) {
          // Recargar mensajes después de reconexión
          this.loadRoomMessages();
        }
      })
    );

    // Chat messages
    this.subscriptions.add(
      this.chatService.messages$.subscribe(messages => {
        if (this.selectedRoom) {
          this.messages = messages.filter(msg => msg.roomId === this.selectedRoom!.id);
          this.scrollToBottom();
        }
      })
    );

    // Active rooms
    this.subscriptions.add(
      this.chatService.activeRooms$.subscribe(rooms => {
        this.activeRooms = rooms;
        if (!this.selectedRoom && rooms.length > 0) {
          this.selectRoom(rooms[0]);
        }
      })
    );

    // Proximity settings
    this.subscriptions.add(
      this.proximityService.settings$.subscribe(settings => {
        this.isProximityEnabled = settings.enabled;
      })
    );

    // WebSocket errors
    this.subscriptions.add(
      this.wsService.errors$.subscribe(error => {
        console.error('WebSocket Error:', error.data);
        this.showError(`Error: ${error.data.message}`);
      })
    );
  }

  private async connectWebSocket(): Promise<void> {
    const token = this.authService.getToken();
    if (token) {
      this.wsService.connect(token);
    } else {
      console.error('No authentication token available');
    }
  }

  // ==================== CHAT ACTIONS ====================

  async joinProximityChat(): Promise<void> {
    this.isLoading = true;
    try {
      // Habilitar notificaciones de proximidad primero
      if (!this.isProximityEnabled) {
        const proximityEnabled = await this.proximityService.enableProximityNotifications(1000);
        if (!proximityEnabled) {
          this.showError('No se pudo habilitar notificaciones de proximidad');
          return;
        }
      }

      // Unirse al chat de proximidad
      const success = await this.chatService.joinProximityChat();
      if (!success) {
        this.showError('No se pudo unir al chat de proximidad');
      }
    } catch (error) {
      console.error('Error joining proximity chat:', error);
      this.showError('Error al unirse al chat de proximidad');
    } finally {
      this.isLoading = false;
    }
  }

  async sendMessage(): Promise<void> {
    if (!this.currentMessage.trim() || !this.selectedRoom) return;

    const messageContent = this.currentMessage.trim();
    this.currentMessage = '';

    try {
      const success = await this.chatService.sendMessage(this.selectedRoom.id, messageContent);
      if (!success) {
        this.showError('No se pudo enviar el mensaje');
        this.currentMessage = messageContent; // Restaurar mensaje
      }
    } catch (error) {
      console.error('Error sending message:', error);
      this.showError('Error al enviar mensaje');
      this.currentMessage = messageContent;
    }
  }

  selectRoom(room: ChatRoom): void {
    this.selectedRoom = room;
    this.chatService.markRoomAsRead(room.id);
    this.loadRoomMessages();
  }

  async loadRoomMessages(): Promise<void> {
    if (!this.selectedRoom) return;

    try {
      const messages = await this.chatService.loadRoomMessages(this.selectedRoom.id);
      // Los mensajes se actualizarán automáticamente vía el observable
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }

  leaveRoom(room: ChatRoom): void {
    const success = this.chatService.leaveRoom(room.id);
    if (success && this.selectedRoom?.id === room.id) {
      this.selectedRoom = null;
      this.messages = [];
    }
  }

  // ==================== UI HELPERS ====================

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.messagesContainer?.nativeElement) {
        const element = this.messagesContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    }, 100);
  }

  private showError(message: string): void {
    // Implementar mostrar error (toast, snackbar, etc.)
    alert(message); // Temporal
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  formatTimestamp(date: Date): string {
    const now = new Date();
    const messageDate = new Date(date);
    
    // Si es hoy, mostrar solo la hora
    if (messageDate.toDateString() === now.toDateString()) {
      return messageDate.toLocaleTimeString('es-ES', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
    
    // Si es de ayer o anterior, mostrar fecha y hora
    return messageDate.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }

  getRoomParticipantCount(room: ChatRoom): number {
    return room.participants.length;
  }

  getConnectionStatusIcon(): string {
    return this.isConnected ? '🟢' : '🔴';
  }

  getConnectionStatusText(): string {
    return this.isConnected ? 'Conectado' : 'Desconectado';
  }

  // ==================== ROOM MANAGEMENT ====================

  async enableProximityNotifications(): Promise<void> {
    try {
      const success = await this.proximityService.enableProximityNotifications(1000);
      if (!success) {
        this.showError('No se pudieron habilitar las notificaciones de proximidad');
      }
    } catch (error) {
      console.error('Error enabling proximity notifications:', error);
      this.showError('Error al habilitar notificaciones');
    }
  }

  async disableProximityNotifications(): Promise<void> {
    try {
      const success = await this.proximityService.disableProximityNotifications();
      if (!success) {
        this.showError('No se pudieron deshabilitar las notificaciones de proximidad');
      }
    } catch (error) {
      console.error('Error disabling proximity notifications:', error);
      this.showError('Error al deshabilitar notificaciones');
    }
  }
}